"use client";
import { useState, useMemo, useEffect, useRef } from "react";
import { FolderTreeItem } from "@zapier/client";
import FolderTreeBranch from "../FolderTreeBranch/FolderTreeBranch";
import { useFolderTreeKeyboardNavigation } from "../../../../../hooks/useFolderTreeKeyboardNavigation";
import styles from "./FolderTree.module.css";

export interface FolderTreeProps {
  folders: FolderTreeItem[];
  selectedFolderId?: string;
  onFolderClick?: (folder: FolderTreeItem) => void;
  defaultExpandedFolders?: string[];
  className?: string;
  "aria-label"?: string;
  isNavigation?: boolean;
  rootName?: string;
  rootId?: string;
  /** When true, the root node acts as a collapsible section header:
   *  clicking anywhere toggles expand, no folder icon, no active state,
   *  and uses subdued text styling. */
  rootAsAccordion?: boolean;
}

const FolderTree = ({
  folders,
  selectedFolderId,
  onFolderClick,
  defaultExpandedFolders = [],
  className,
  "aria-label": ariaLabel = "Folder navigation",
  isNavigation = false,
  rootName = "Folders",
  rootId = "root",
  rootAsAccordion = false,
}: FolderTreeProps) => {
  const treeReference = useRef<HTMLElement>(null);

  const [expandedFolders, setExpandedFolders] = useState<
    Set<string | undefined>
  >(() => {
    // Start with defaultExpandedFolders
    // Root starts closed unless explicitly in defaultExpandedFolders
    return new Set(defaultExpandedFolders);
  });

  useEffect(() => {
    // Merge new defaultExpandedFolders with existing expandedFolders
    // This preserves user-expanded folders while ensuring ancestors are expanded
    setExpandedFolders((previous) => {
      const newSet = new Set(previous);
      for (const folderId of defaultExpandedFolders) {
        newSet.add(folderId);
      }
      return newSet;
    });
  }, [defaultExpandedFolders]);

  const handleToggleExpanded = (folderId?: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const rootFoldersItem: FolderTreeItem = useMemo(
    () =>
      ({
        id: rootId,
        name: rootName,
        accountId: 0,
        ownerId: 0,
        isShared: false,
        children: folders,
      }) as FolderTreeItem,
    [folders, rootId, rootName],
  );

  const rootFolders = [rootFoldersItem];

  const { focusableFolderId, handleKeyDown, getFocusableTabIndex } =
    useFolderTreeKeyboardNavigation({
      folders: rootFolders,
      selectedFolderId,
      expandedFolders,
      onToggleExpanded: handleToggleExpanded,
      onFolderClick,
      treeRef: treeReference,
      isRoot: true,
    });
  return (
    <nav
      className={`${styles.folderTreeContainer} ${className || ""}`}
      aria-label={ariaLabel}
    >
      <FolderTreeBranch
        folders={rootFolders}
        selectedFolderId={selectedFolderId}
        onFolderClick={onFolderClick}
        isRoot={true}
        expandedFolders={expandedFolders}
        onToggleExpanded={handleToggleExpanded}
        isNavigation={isNavigation}
        rootAsAccordion={rootAsAccordion}
        focusableFolderId={focusableFolderId}
        handleKeyDown={handleKeyDown}
        getFocusableTabIndex={getFocusableTabIndex}
        treeRef={treeReference}
      />
    </nav>
  );
};

export default FolderTree;
